import React, { useState, useContext } from 'react';
import { NavContext } from '../../../NavContextWrapper';
import { performLogout } from '../utils/performLogout';
import type { Client } from '../../../NavContextWrapper/types';

type ModalState =
  | { status: 'idle' }
  | { status: 'modal_open' }
  | { status: 'error'; message?: string };

interface UseSignOutModalOptions {
  client: Client;
  onPopoverClose: () => void;
}

interface LogoutButtonProps {
  onClick: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
  ) => void;
}

interface SignOutModalProps {
  client: Client;
  isOpen: boolean;
  onClose: () => void;
  onConfirmLogout: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
  ) => Promise<void>;
}

interface ErrorToastProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UseSignOutModalReturn {
  logoutButtonProps: LogoutButtonProps;
  signOutModalProps: SignOutModalProps;
  errorToastProps: ErrorToastProps;
}

/**
 * Custom hook that manages sign-out modal state and logout logic for dropdown menus.
 * Centralizes logout handling, modal management, and error states to avoid duplication
 * across multiple dropdown components.
 *
 * @param options - Configuration options for the hook
 * @param options.client - The current user client object
 * @param options.onPopoverClose - Callback to close the parent popover when modal opens
 * @returns Object containing logout handlers and modal render function
 */
export const useSignOutModal = ({
  client,
  onPopoverClose,
}: UseSignOutModalOptions): UseSignOutModalReturn => {
  const { rootUrl } = useContext(NavContext);
  const [modalState, setModalState] = useState<ModalState>({
    status: 'idle',
  });

  const shouldShowSignOutModal = client?.capabilities?.needsOnboarding;

  const handleLogout = async (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();

    try {
      await performLogout(rootUrl);
      // If successful, performLogout will redirect, so we won't reach here
      // But just in case, reset state
      setModalState({ status: 'idle' });
    } catch (error) {
      setModalState({
        status: 'error',
        message: "We couldn't sign you out. Please try again.",
      });
    }
  };

  const handleSignOutClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();

    if (shouldShowSignOutModal) {
      setModalState({ status: 'modal_open' });
      onPopoverClose();
    } else {
      onPopoverClose();
      handleLogout(e);
    }
  };

  const logoutButtonProps: LogoutButtonProps = {
    onClick: handleSignOutClick,
  };

  const signOutModalProps: SignOutModalProps = {
    client,
    isOpen: modalState.status === 'modal_open',
    onClose: () => setModalState({ status: 'idle' }),
    onConfirmLogout: handleLogout,
  };

  const errorToastProps: ErrorToastProps = {
    isOpen: modalState.status === 'error',
    onClose: () => setModalState({ status: 'idle' }),
  };

  return {
    logoutButtonProps,
    signOutModalProps,
    errorToastProps,
  };
};
